import React from "react";
import { Box, Text, Flex } from "@chakra-ui/react";

export interface HoverInfoTooltipProps {
  title: string;
  description: string;
  position?: "top" | "bottom" | "left" | "right" | "center";
}

/**
 * Lightweight hover tooltip component for showing info on hover
 * More subtle and less prominent than the tour guide tooltip
 */
export const HoverInfoTooltip: React.FC<HoverInfoTooltipProps> = ({
  title,
  description,
  position = "right",
}) => {
  // Arrow positioning based on tooltip position
  const getArrowStyles = () => {
    switch (position) {
      case "right":
        return {
          _before: {
            content: '""',
            position: "absolute",
            left: "-8px",
            top: "50%",
            transform: "translateY(-50%)",
            width: "0",
            height: "0",
            borderTop: "8px solid transparent",
            borderBottom: "8px solid transparent",
            borderRight: "8px solid #E5E5E5",
          },
          _after: {
            content: '""',
            position: "absolute",
            left: "-7px",
            top: "50%",
            transform: "translateY(-50%)",
            width: "0",
            height: "0",
            borderTop: "8px solid transparent",
            borderBottom: "8px solid transparent",
            borderRight: "8px solid white",
          },
        };
      case "left":
        return {
          _before: {
            content: '""',
            position: "absolute",
            right: "-8px",
            top: "50%",
            transform: "translateY(-50%)",
            width: "0",
            height: "0",
            borderTop: "8px solid transparent",
            borderBottom: "8px solid transparent",
            borderLeft: "8px solid #E5E5E5",
          },
          _after: {
            content: '""',
            position: "absolute",
            right: "-7px",
            top: "50%",
            transform: "translateY(-50%)",
            width: "0",
            height: "0",
            borderTop: "8px solid transparent",
            borderBottom: "8px solid transparent",
            borderLeft: "8px solid white",
          },
        };
      case "top":
        return {
          _before: {
            content: '""',
            position: "absolute",
            bottom: "-8px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "0",
            height: "0",
            borderLeft: "8px solid transparent",
            borderRight: "8px solid transparent",
            borderTop: "8px solid #E5E5E5",
          },
          _after: {
            content: '""',
            position: "absolute",
            bottom: "-7px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "0",
            height: "0",
            borderLeft: "8px solid transparent",
            borderRight: "8px solid transparent",
            borderTop: "8px solid white",
          },
        };
      case "bottom":
        return {
          _before: {
            content: '""',
            position: "absolute",
            top: "-8px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "0",
            height: "0",
            borderLeft: "8px solid transparent",
            borderRight: "8px solid transparent",
            borderBottom: "8px solid #E5E5E5",
          },
          _after: {
            content: '""',
            position: "absolute",
            top: "-7px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "0",
            height: "0",
            borderLeft: "8px solid transparent",
            borderRight: "8px solid transparent",
            borderBottom: "8px solid white",
          },
        };
      default:
        return {};
    }
  };

  return (
    <Box
      bg="white"
      borderRadius="8px"
      boxShadow="0px 2px 12px rgba(0, 0, 0, 0.1)"
      border="1px solid #E5E5E5"
      p="12px 16px"
      minW="280px"
      maxW="320px"
      position="relative"
      zIndex={10000}
      {...getArrowStyles()}
    >
      <Flex flexDirection="column" gap="4px">
        <Text fontSize="13px" fontWeight="600" color="#1A1A1A" lineHeight="1.4">
          {title}
        </Text>
        <Text fontSize="12px" color="#666666" lineHeight="1.5">
          {description}
        </Text>
      </Flex>
    </Box>
  );
};
